import React, { useState, useEffect } from 'react';
import { ChevronLeft, Loader, Lock, Shield, CreditCard, Calendar, Clock, Building2, Store, Check, Star, Tag, X } from 'lucide-react';
import {
  formatCardNumber, formatExpiry, formatCVV, validateCardData,
  getDurationMinutes, getExtraConsultationPrice, getSpecialtyPrice,
} from '../../../utils/checkoutHelpers';
import {
  processConsultationPayment, showPaymentError, showPaymentSuccess,
  validateCoupon, getFreshAuthToken,
} from '../../../utils/paymentService';
import {
  validateExtraConsultationAmount,
  getAvailableProcessorsForConsultationDate,
  createExtraConsultation,
  resolveExtraDurationId,
} from '../../../services/extraConsultationService';
import WhatsappVerificationModal from '../../WhatsappVerificationModal';

const formatMoney = (n) => Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const STORE_TOO_CLOSE = 'No disponible: tu consulta es en menos de 48 h y este método tarda hasta 48 h en confirmar.';

// El backend devuelve items granulares (varias tiendas, varias tarjetas). Los
// agrupamos en categorías lógicas (paridad con la app): tarjeta / transferencia
// / pago en tiendas / paypal. La tarjeta (is_card_method) es el único método
// funcional hoy (OpenPay); el resto se muestra deshabilitado.
const describeProcessor = (proc) => {
  const code = (proc.code || '').toLowerCase();
  const tooClose = proc.unavailableReason === 'STORE_PAYMENT_TOO_CLOSE';
  const blockedSub = proc.available
    ? 'Disponible próximamente en la web'
    : (tooClose ? STORE_TOO_CLOSE : 'No disponible para esta fecha');
  if (proc.isCard) {
    return { key: 'card', icon: CreditCard, label: 'Tarjeta de crédito o débito', sub: 'Pago inmediato', payable: true, available: proc.available };
  }
  if (code.includes('paypal')) {
    return { key: 'paypal', icon: CreditCard, label: 'PayPal', sub: 'Disponible próximamente en la web', payable: false, available: proc.available };
  }
  if (code.includes('transfer')) {
    return { key: 'transfer', icon: Building2, label: 'Transferencia interbancaria', sub: blockedSub, payable: false, available: proc.available };
  }
  if (code.includes('store') || code.includes('oxxo')) {
    return { key: 'store', icon: Store, label: 'Pago en tiendas', sub: blockedSub, payable: false, available: proc.available };
  }
  return { key: code || `proc-${proc.id}`, icon: CreditCard, label: proc.title || 'Otro método', sub: blockedSub, payable: false, available: proc.available };
};

const CheckoutPage = ({
  checkoutData,
  userData,
  selectedSpecialty,
  selectedDate,
  selectedTime,
  selectedSlot,
  selectedDoctor,
  specialtyConfig,
  onPaymentComplete,
  onBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [priceInfo, setPriceInfo] = useState(null);
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [reason, setReason] = useState(userData?.consultationReason || '');
  const [reminder, setReminder] = useState(true);
  const [cardData, setCardData] = useState({ number: '', name: '', expiry: '', cvv: '' });

  // Cupón (mismo patrón que MembershipCheckout / checkout de membresía).
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');

  // Gate V2 — verificación de teléfono al crear la consulta.
  const [showWhatsappGate, setShowWhatsappGate] = useState(false);
  const pendingPayRef = React.useRef(false);

  const specialty = specialtyConfig?.[selectedSpecialty] || {};
  const specialtyName = specialty.name || 'Consulta';

  const slot = selectedSlot || {};
  const durationId = slot.duration_id ?? slot.durationId ?? specialty.duration;
  const scheduleId = slot.scheduleId ?? slot.id ?? slot.slotId;

  // Doctora: en el flujo "por fecha" `selectedDoctor` puede venir null, pero el
  // slot trae la doctora. Resolvemos para mostrar SIEMPRE su foto.
  const doctor =
    selectedDoctor ||
    slot.doctor ||
    (slot.professional_ids?.length === 1 ? slot.professional_ids[0] : null);
  const doctorId =
    doctor?.id || slot.professional_ids?.[0]?.id || slot.professional_id ||
    selectedDoctor?.firebaseId;
  const doctorName = (
    doctor ? `${doctor.name || ''} ${doctor.last_name || ''}`.trim() : ''
  ) || slot.professional_ids?.[0]?.name || specialtyName;
  const doctorSpecialty = doctor?.specialty?.name || specialtyName;
  const doctorRating = doctor?.rating_professional;
  const avatarFallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(doctorName || 'Dra')}&background=786ED9&color=fff&size=128`;
  const doctorAvatar = doctor?.avatar || doctor?.image || doctor?.image_url || avatarFallback;

  const authToken = userData?.authToken || localStorage.getItem('daisies_auth_token') || '';
  const userId = userData?.userId || userData?.daisUserId || localStorage.getItem('daisies_user_id') || '';

  // ─── Precio autoritativo + métodos disponibles (gating 48h) ───
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingMeta(true);
      const [price, procs] = await Promise.all([
        validateExtraConsultationAmount({ authToken, userId, durationId, scheduleId }),
        getAvailableProcessorsForConsultationDate({ authToken, scheduleId }),
      ]);
      if (cancelled) return;
      if (price.success) setPriceInfo({ price: price.price });

      let list = [];
      if (procs.success && procs.items.length > 0) {
        list = procs.items.map((p) => describeProcessor(p));
        const seen = new Set();
        list = list.filter((m) => (seen.has(m.key) ? false : seen.add(m.key)));
      }
      if (!list.some((m) => m.key === 'card')) {
        list.unshift({ key: 'card', icon: CreditCard, label: 'Tarjeta de crédito o débito', sub: 'Pago inmediato', payable: true, available: true });
      }
      setMethods(list);
      setSelectedMethod('card');
      setLoadingMeta(false);
    };
    load().catch(() => { if (!cancelled) setLoadingMeta(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Precio base: backend manda; fallback local sólo si el backend no respondió.
  const localPrice =
    getExtraConsultationPrice(specialty, getDurationMinutes(specialty, durationId), false) ??
    checkoutData?.total ?? getSpecialtyPrice();
  const baseAmount = priceInfo?.price > 0 ? priceInfo.price : localPrice;

  const discount = !appliedCoupon ? 0
    : appliedCoupon.discountType === 'amount'
      ? Math.min(baseAmount, appliedCoupon.discountAmount || 0)
      : Math.round(baseAmount * ((appliedCoupon.discountPercentage || 0) / 100));
  const totalAmount = Math.max(0, baseAmount - discount);

  const dateLabel = selectedDate
    ? `${selectedDate.getDate()} de ${selectedDate.toLocaleDateString('es-MX', { month: 'short' })}`
    : '';

  const handleCardInputChange = (field, value) => {
    let formatted = value;
    if (field === 'number') formatted = formatCardNumber(value);
    else if (field === 'expiry') formatted = formatExpiry(value);
    else if (field === 'cvv') formatted = formatCVV(value);
    setCardData((prev) => ({ ...prev, [field]: formatted }));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) { setCouponError('Ingresa un código'); return; }
    setCouponError('');
    setValidatingCoupon(true);
    try {
      const freshToken = await getFreshAuthToken().catch(() => authToken);
      const extraDurationId = resolveExtraDurationId(specialty, durationId);
      if (!doctorId || !extraDurationId) throw new Error('Faltan datos de la consulta');
      const result = await validateCoupon(couponCode, doctorId, userId, extraDurationId, freshToken);
      if (result.success) {
        setAppliedCoupon(result.coupon);
        setCouponCode('');
      } else {
        setCouponError(result.error || 'Cupón no válido');
      }
    } catch (err) {
      setCouponError(err.message || 'Error al validar');
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Crea el draft de la cita (mismo slot, duración extra) y la cobra.
  const runCreateAndPay = async () => {
    if (!userId || !authToken) throw new Error('No se encontró tu sesión. Recarga la página e intenta de nuevo.');
    const extraDurationId = resolveExtraDurationId(specialty, durationId);
    const created = await createExtraConsultation({
      authToken, userId, doctorId,
      date: slot.date || selectedDate,
      extraDurationId, scheduleId,
      reason: reason.trim(), reminder,
    });
    if (!created.success) {
      if (created.phoneNotVerified) return { needsPhone: true };
      throw new Error(created.error || 'No se pudo crear la consulta.');
    }
    const payment = await processConsultationPayment(
      cardData, created.consultationId, null, userId, appliedCoupon?.id || null,
    );
    if (!payment.success) throw new Error(payment.error || 'No se pudo procesar el pago.');
    return { success: true, transactionId: payment.transactionId, cardLast4: payment.cardLast4, consultationData: created.data };
  };

  const completePayment = (result) => {
    onPaymentComplete({
      success: true,
      transactionId: result.transactionId,
      cardLast4: result.cardLast4,
      amount: totalAmount,
      originalAmount: baseAmount,
      appliedCoupon,
      hasMembership: false,
      consultationData: result.consultationData,
    });
  };

  const handlePayment = async () => {
    const validation = validateCardData(cardData);
    if (!validation.isValid) return showPaymentError(validation.message);
    setLoading(true);
    try {
      const result = await runCreateAndPay();
      if (result?.needsPhone) { pendingPayRef.current = true; setShowWhatsappGate(true); return; }
      await showPaymentSuccess();
      completePayment(result);
    } catch (error) {
      showPaymentError(error.message || 'Error al procesar el pago. Intenta de nuevo o contacta a soporte.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneVerified = async () => {
    setShowWhatsappGate(false);
    if (!pendingPayRef.current) return;
    pendingPayRef.current = false;
    setLoading(true);
    try {
      const result = await runCreateAndPay();
      if (result?.needsPhone) throw new Error('No pudimos verificar tu teléfono. Intenta de nuevo.');
      await showPaymentSuccess();
      completePayment(result);
    } catch (error) {
      showPaymentError(error.message || 'No se pudo procesar el pago. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const payDisabled = loading || loadingMeta || selectedMethod !== 'card';

  return (
    <div className="max-w-lg mx-auto px-4 py-6 animate-fadeIn">
      <button
        onClick={onBack}
        className="mb-5 text-daisies hover:text-daisies-hover font-medium flex items-center gap-1.5 transition-all text-sm active:scale-[0.97]"
      >
        <ChevronLeft size={16} />
        Volver
      </button>

      {/* Resumen de la cita */}
      <div className="bg-surface-card rounded-card p-5 mb-4">
        <div className="flex items-center gap-4">
          <img
            src={doctorAvatar}
            alt={doctorName}
            className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-soft flex-shrink-0"
            onError={(e) => { e.target.src = avatarFallback; }}
          />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-text-primary truncate">{doctorName}</p>
            <p className="text-text-secondary text-sm truncate">{doctorSpecialty}</p>
          </div>
          {doctorRating ? (
            <div className="flex items-center gap-1 flex-shrink-0 bg-amber-pill/25 rounded-full px-2 py-1">
              <Star size={13} className="fill-amber-pill text-amber-pill" />
              <span className="text-xs font-bold text-amber-deep">{Number(doctorRating).toFixed(1)}</span>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-brand-tint-100">
          <div className="flex items-center gap-1.5 text-text-secondary text-sm">
            <Calendar size={14} className="text-daisies" />
            <span className="capitalize">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-1.5 text-text-secondary text-sm">
            <Clock size={14} className="text-daisies" />
            <span>{selectedTime}</span>
          </div>
        </div>
      </div>

      {/* Total a pagar + cupón */}
      <div className="bg-brand-tint rounded-card p-5 mb-4">
        <p className="ds-caption text-brand-deep/70 uppercase tracking-wider mb-1">Total a pagar</p>
        {loadingMeta ? (
          <div className="flex items-center gap-2 text-text-secondary">
            <Loader className="animate-spin" size={18} /> <span className="text-sm">Calculando precio...</span>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2 flex-wrap">
              <p className="text-3xl font-bold text-text-primary leading-none">
                ${formatMoney(totalAmount)} <span className="text-base font-semibold text-text-secondary">MXN</span>
              </p>
              {discount > 0 && (
                <span className="text-base text-text-secondary line-through mb-0.5">${formatMoney(baseAmount)}</span>
              )}
            </div>
            <span className="inline-block mt-2 text-xs font-bold text-brand-deep bg-surface-card rounded-full px-2.5 py-1">
              Precio sin membresía
            </span>

            {/* Cupón */}
            {appliedCoupon ? (
              <div className="mt-3 flex items-center justify-between bg-mint-pill rounded-input px-3 py-2 animate-slideDown">
                <div className="flex items-center gap-2 min-w-0">
                  <Check size={14} className="text-mint-deep flex-shrink-0" />
                  <span className="text-sm font-semibold text-mint-deep truncate">{appliedCoupon.code}</span>
                  <span className="text-xs text-mint-deep/80 flex-shrink-0">
                    -{appliedCoupon.discountType === 'amount'
                      ? `$${formatMoney(appliedCoupon.discountAmount)}`
                      : `${appliedCoupon.discountPercentage}%`}
                  </span>
                </div>
                <button
                  onClick={() => { setAppliedCoupon(null); setCouponError(''); }}
                  className="text-mint-deep/70 hover:text-mint-deep transition-colors flex-shrink-0"
                  aria-label="Quitar cupón"
                >
                  <X size={16} />
                </button>
              </div>
            ) : showCoupon ? (
              <div className="mt-3 animate-slideDown">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleApplyCoupon(); }}
                    placeholder="Código de descuento"
                    disabled={validatingCoupon}
                    className="flex-1 px-3 py-2.5 bg-surface-card rounded-input text-sm uppercase focus:outline-none focus:ring-2 focus:ring-brand transition-all"
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    className="px-4 py-2.5 bg-daisies text-white rounded-input font-semibold text-sm hover:bg-daisies-hover transition-all disabled:opacity-40 active:scale-[0.97] flex items-center gap-1.5"
                  >
                    {validatingCoupon ? <Loader className="animate-spin" size={14} /> : 'Aplicar'}
                  </button>
                </div>
                {couponError && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><X size={11} /> {couponError}</p>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowCoupon(true)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-daisies font-semibold hover:text-daisies-hover transition-colors"
              >
                <Tag size={14} /> ¿Tienes un cupón?
              </button>
            )}
          </>
        )}
      </div>

      {/* Motivo de la consulta */}
      <div className="bg-white rounded-card p-5 mb-4">
        <label className="block font-semibold text-text-primary mb-2">Motivo de la consulta</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Cuéntale a tu doctora qué necesitas (opcional)"
          className="w-full p-4 rounded-input bg-warm-canvas focus:ring-2 focus:ring-brand focus:outline-none text-text-primary resize-none transition-all"
        />
        <label className="flex items-center gap-2.5 cursor-pointer group mt-3">
          <span className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${reminder ? 'bg-daisies' : 'bg-warm-canvas ring-1 ring-brand-tint-100 group-hover:ring-brand'}`}>
            {reminder && <Check size={14} className="text-white" />}
          </span>
          <input type="checkbox" className="hidden" checked={reminder} onChange={(e) => setReminder(e.target.checked)} />
          <span className="text-sm text-text-secondary">Recordarme antes de mi cita</span>
        </label>
      </div>

      {/* Elige cómo pagar */}
      <div className="mb-4">
        <h3 className="font-semibold text-text-primary mb-3">Elige cómo pagar</h3>
        <div className="space-y-2.5">
          {methods.map((m) => {
            const Icon = m.icon;
            const isSelected = selectedMethod === m.key;
            const disabled = !m.payable;
            return (
              <button
                key={m.key}
                onClick={() => { if (!disabled) setSelectedMethod(m.key); }}
                disabled={disabled}
                className={`w-full text-left p-4 rounded-card flex items-center gap-3 transition-all ${
                  isSelected ? 'ring-2 ring-brand bg-daisies-50' : 'bg-white'
                } ${disabled ? 'opacity-55 cursor-not-allowed' : 'hover:bg-daisies-50'}`}
              >
                <div className={`p-2.5 rounded-input flex-shrink-0 ${isSelected ? 'bg-daisies text-white' : 'bg-warm-canvas text-daisies'}`}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-text-primary">{m.label}</span>
                    {m.key === 'card' && (
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-daisies text-white rounded-full px-2 py-0.5">Recomendado</span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary mt-0.5">{m.sub}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Formulario de tarjeta */}
      {selectedMethod === 'card' && (
        <div className="bg-white rounded-card p-5 mb-4">
          <div className="flex items-center gap-2 mb-5">
            <CreditCard size={18} className="text-daisies" />
            <h3 className="font-semibold text-text-primary">Tarjeta de pago</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">Número de tarjeta</label>
              <input
                type="text" inputMode="numeric" value={cardData.number}
                onChange={(e) => handleCardInputChange('number', e.target.value)}
                placeholder="0000 0000 0000 0000"
                className="w-full px-4 py-3 bg-warm-canvas rounded-input text-base focus:outline-none focus:ring-2 focus:ring-brand transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">Nombre en la tarjeta</label>
              <input
                type="text" value={cardData.name}
                onChange={(e) => handleCardInputChange('name', e.target.value)}
                placeholder="NOMBRE APELLIDO"
                className="w-full px-4 py-3 bg-warm-canvas rounded-input text-base focus:outline-none focus:ring-2 focus:ring-brand transition-all"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">Vencimiento</label>
                <input
                  type="text" inputMode="numeric" value={cardData.expiry}
                  onChange={(e) => handleCardInputChange('expiry', e.target.value)}
                  placeholder="MM/AA"
                  className="w-full px-4 py-3 bg-warm-canvas rounded-input text-base focus:outline-none focus:ring-2 focus:ring-brand transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">CVV</label>
                <input
                  type="text" inputMode="numeric" value={cardData.cvv}
                  onChange={(e) => handleCardInputChange('cvv', e.target.value)}
                  placeholder="123"
                  className="w-full px-4 py-3 bg-warm-canvas rounded-input text-base focus:outline-none focus:ring-2 focus:ring-brand transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagar */}
      <button
        onClick={handlePayment}
        disabled={payDisabled}
        className="w-full py-4 bg-daisies text-white rounded-card font-bold text-[17px] hover:bg-daisies-hover active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 mb-3"
      >
        {loading ? (
          <><Loader className="animate-spin" size={20} /> Procesando...</>
        ) : (
          <><Lock size={17} /> Pagar ${formatMoney(totalAmount)} MXN</>
        )}
      </button>

      <div className="flex items-center justify-center gap-1.5 text-text-secondary text-[11px]">
        <Shield size={11} />
        <span>Pago seguro con encriptación SSL</span>
      </div>

      {showWhatsappGate && (
        <WhatsappVerificationModal
          userId={parseInt(userId, 10)}
          authToken={authToken}
          phone={userData?.telefono || userData?.phone}
          onVerified={handlePhoneVerified}
          onCancel={() => { setShowWhatsappGate(false); pendingPayRef.current = false; }}
        />
      )}
    </div>
  );
};

export default CheckoutPage;
